/**
 * DebtProjector: debt balances and payments over time (CONVENTIONS §5.2, R14, R15, TIMELINE R23).
 * Every function takes the engine clock `now` explicitly. Balances are the closed form of interest
 * then the user's payment (`amount`, once a month whatever the frequency), floored at 0, from a
 * loan's drawdown or a balance's own day, piecewise between dated rate changes; a debt is paid on
 * its own payment dates at what that balance takes.
 */
import { addDays, addMonths, maxDay, minDay, monthsBetween, startOfMonth, type Day } from '../core/calendar.ts';
import {
  abs, add, eq, fromInt, gt, isZero, max, min, mul, round2, sub, sum, ZERO, type Decimal,
} from '../core/decimal.ts';
import { timeBuckets } from '../forecast/window.ts';
import { frequencyStep, scheduleDate } from '../models/frequency.ts';
import type { Id, Series, Transaction, Window } from '../types/domain.ts';
import { effectiveInterestRate, fieldsOn } from '../models/transaction.ts';
import { balanceAfterPayments, monthlyPayment, monthlyRate } from './loan-math.ts';

export const MAX_PAYOFF_MONTHS = 600;

/** DebtProjector's month difference: whole months, never negative. */
export const elapsedMonths = (from: Day, to: Day): number => Math.max(0, monthsBetween(from, to));

export const debtMonthlyRate = (debt: Transaction): Decimal => monthlyRate(debt.interestRate ?? ZERO);

const nonZeroAbs = (value: Decimal | null): Decimal | null => (value !== null && !isZero(value) ? abs(value) : null);

/**
 * First non-zero of |debtCurrentBalance|, |debtPrincipal|, |initialBalance|; else
 * amount × (endAfterOccurrences ?? debtTermMonths ?? debtRemainingMonths); else 0.
 */
export function effectiveInitialBalance(debt: Transaction): Decimal {
  const stored = nonZeroAbs(debt.debtCurrentBalance) ?? nonZeroAbs(debt.debtPrincipal) ?? nonZeroAbs(debt.initialBalance);
  if (stored !== null) return stored;
  const occurrences = debt.endAfterOccurrences ?? debt.debtTermMonths ?? debt.debtRemainingMonths;
  return occurrences === null ? ZERO : mul(debt.amount, fromInt(occurrences));
}

export const effectiveStartDate = (debt: Transaction): Day => debt.debtStartDate ?? debt.startDate;

export const effectiveTermMonths = (debt: Transaction): number | null =>
  debt.debtRemainingMonths ?? debt.debtTermMonths ?? debt.endAfterOccurrences;

/** Dates of a monthly schedule anchored at the start on or before `date`, within endDate and endAfterOccurrences. */
function scheduledPaymentCount(debt: Transaction, date: Day): number {
  if (date < debt.startDate) return 0;
  if (debt.endDate !== null && date > debt.endDate) return scheduledPaymentCount(debt, debt.endDate);
  const count = monthsBetween(debt.startDate, date) + 1;
  return debt.endAfterOccurrences === null ? count : Math.min(count, Math.max(0, debt.endAfterOccurrences));
}

/**
 * Payments a current-balance debt makes after `anchor` up to `date` (R14): its monthly schedule's
 * dates in (anchor, date]; other frequencies step monthly from `anchor` (D18).
 */
const paymentsSince = (debt: Transaction, date: Day, anchor: Day): number =>
  debt.frequency !== 'monthly'
    ? elapsedMonths(anchor, date)
    : Math.max(0, scheduledPaymentCount(debt, date) - scheduledPaymentCount(debt, anchor));

/**
 * Payments a current-balance debt has left after `anchor` (R15): debtRemainingMonths or, for a
 * monthly debt with an end, what is left of its schedule, the fewer; null when neither bounds it.
 */
function remainingPayments(debt: Transaction, anchor: Day): number | null {
  if (debt.frequency !== 'monthly') return debt.debtRemainingMonths;
  const scheduleLength =
    debt.endDate !== null
      ? scheduledPaymentCount(debt, debt.endDate)
      : debt.endAfterOccurrences === null
        ? null
        : Math.max(0, debt.endAfterOccurrences);
  if (scheduleLength === null) return debt.debtRemainingMonths;
  const left = Math.max(0, scheduleLength - scheduledPaymentCount(debt, anchor));
  return Math.min(debt.debtRemainingMonths ?? left, left);
}

const entersByBalance = (debt: Transaction): boolean => debt.debtEntryMode === 'currentBalance' && debt.debtCurrentBalance !== null;

/**
 * The day a debt entered by its balance was true (TIMELINE R23): its balance day, else the clock's
 * day. Its payments count from the end of that day, so the balance moves with the calendar.
 */
export const balanceDay = (debt: Transaction, now: Day): Day => debt.debtBalanceDate ?? now;

/** How a debt's payments are counted from the balance they start from. */
interface Amortisation {
  readonly opening: Decimal;
  /** Payments made after the opening balance up to the end of `date`. */
  readonly paymentsBy: (date: Day) => number;
  readonly paymentDate: (payment: number) => Day;
  /** The clearing payment's number (the term, or the payments left), or null. */
  readonly last: number | null;
  /** Whether the regular payment is re-priced when the rate changes (decision 4: a loan with a term). */
  readonly reprices: boolean;
}

function amortisation(debt: Transaction, now: Day): Amortisation {
  const typed = debt.debtCheckIn;
  if (typed) {
    const base = amortisation({ ...debt, debtCheckIn: null }, now);
    const before = base.paymentsBy(typed.date);
    return {
      opening: abs(typed.balance),
      paymentsBy: (date) => base.paymentsBy(date) - before,
      paymentDate: (k) => base.paymentDate(before + k),
      last: base.last === null ? null : base.last - before,
      reprices: base.reprices,
    };
  }
  if (entersByBalance(debt)) {
    const anchor = balanceDay(debt, now);
    const before = scheduledPaymentCount(debt, anchor);
    const monthly = debt.frequency === 'monthly';
    return {
      opening: abs(debt.debtCurrentBalance!),
      paymentsBy: (date) => paymentsSince(debt, date, anchor),
      paymentDate: (k) => (monthly ? scheduleDate(debt.startDate, { unit: 'month', count: 1 }, before + k - 1) : addMonths(anchor, k)),
      last: remainingPayments(debt, anchor),
      reprices: false,
    };
  }
  const start = effectiveStartDate(debt);
  const term = effectiveTermMonths(debt);
  return {
    opening: effectiveInitialBalance(debt),
    paymentsBy: (date) => elapsedMonths(start, date),
    paymentDate: (k) => addMonths(start, k),
    last: term,
    reprices: debt.debtEntryMode !== 'currentBalance' && term !== null,
  };
}

const rateOnPayment = (debt: Transaction, a: Amortisation, k: number): Decimal => effectiveInterestRate(debt, a.paymentDate(k)) ?? ZERO;

/** The payment numbers a check-in recorded (R27), with what was paid: only days the schedule pays on. */
function recordedPayments(debt: Transaction, a: Amortisation): ReadonlyMap<number, Decimal> {
  const recorded = new Map<number, Decimal>();
  for (const [date, paid] of debt.actuals ?? []) {
    const n = a.paymentsBy(date);
    if (n >= 1 && a.paymentDate(n) === date) recorded.set(n, paid);
  }
  return recorded;
}

/**
 * The balance after `k` payments and the regular payment in force for the k-th (R23, R24, decision 4):
 * each payment is charged the rate in force on its day (the base rate, or the latest
 * interestRateChanges entry on or before it) and is the payment typed for that day (a dated change
 * of `amount`); between changes the closed form runs as before. A loan with a term re-prices its
 * payment over the payments left when its rate changes; any other debt keeps its typed payment. A
 * payment a check-in recorded (R27) pays what was recorded, at most what is due, and changes nothing after it.
 */
function amortised(debt: Transaction, a: Amortisation, k: number): { readonly balance: Decimal; readonly payment: Decimal } {
  const firstOnOrAfter = (date: Day): number => a.paymentsBy(addDays(date, -1)) + 1;
  const typedOn = (n: number): Decimal => fieldsOn(debt, a.paymentDate(n)).amount;
  const typedFrom = new Set((debt.history ?? []).filter((c) => c.was.amount !== undefined).map((c) => firstOnOrAfter(c.from)));
  const rateFrom = (debt.interestRateChanges ?? []).map((c) => firstOnOrAfter(c.date));
  const recorded = recordedPayments(debt, a);
  const around = [...recorded.keys()].flatMap((n) => [n, n + 1]);
  const starts = [...new Set([1, ...rateFrom, ...typedFrom, ...around].filter((n) => n >= 1))].sort((x, y) => x - y);
  let balance = a.opening;
  let payment = typedOn(1);
  let previousRate = debt.interestRate ?? ZERO;
  for (const [i, from] of starts.entries()) {
    if (from > k || !gt(balance, ZERO)) break;
    const to = Math.min(k, (starts[i + 1] ?? Number.POSITIVE_INFINITY) - 1);
    const rate = rateOnPayment(debt, a, from);
    if (typedFrom.has(from)) payment = typedOn(from);
    if (a.reprices && a.last !== null && !eq(rate, previousRate)) {
      payment = round2(monthlyPayment({ principal: balance, annualRatePercent: rate, termMonths: Math.max(1, a.last - from + 1) }) ?? balance);
    }
    previousRate = rate;
    const paid = recorded.get(from);
    balance = balanceAfterPayments(balance, paid ?? payment, monthlyRate(rate), paid === undefined ? to - from + 1 : 1);
  }
  return { balance, payment };
}

/**
 * Balance at the end of `date` from first principles (Swift's private calculateCurrentBalance): 0
 * before the start month; the initial balance for interest-only debts; otherwise the closed form
 * from a loan's drawdown, or from the end of the balance day of a debt entered by its balance (R23).
 */
export function currentBalance(debt: Transaction, date: Day, now: Day): Decimal {
  if (debt.debtCheckIn && date < debt.debtCheckIn.date) return currentBalance({ ...debt, debtCheckIn: null }, date, now);
  const initial = effectiveInitialBalance(debt);
  if (!gt(initial, ZERO)) return ZERO;
  if (startOfMonth(date) < startOfMonth(effectiveStartDate(debt))) return ZERO;
  if (debt.debtIsInterestOnly === true) return debt.debtCheckIn ? abs(debt.debtCheckIn.balance) : initial;

  const a = amortisation(debt, now);
  const k = a.paymentsBy(date);
  if (k <= 0) return a.opening;
  const cleared = entersByBalance(debt) || debt.debtCheckIn ? a.last !== null && a.last > 0 && k >= a.last : a.last !== null && k >= a.last;
  if (cleared) return ZERO;
  return amortised(debt, a, k).balance;
}

/** The regular payment in force on a payment date: the typed amount, re-priced for a loan after a rate change. */
function regularPayment(debt: Transaction, date: Day, now: Day): Decimal {
  if (debt.debtCheckIn && date <= debt.debtCheckIn.date) return regularPayment({ ...debt, debtCheckIn: null }, date, now);
  const a = amortisation(debt, now);
  const k = a.paymentsBy(date);
  return k <= 0 ? fieldsOn(debt, date).amount : amortised(debt, a, k).payment;
}

/**
 * Remaining balance at `date`, unrounded. On a debt's balance day a stored non-zero
 * debtCurrentBalance wins (the ledger shortcut); before a loan's drawdown nothing is owed yet (R23),
 * before a balance-entered debt's start its balance.
 */
export function remainingBalance(debt: Transaction, date: Day, now: Day): Decimal {
  const ledger = date === balanceDay(debt, now) ? nonZeroAbs(debt.debtCurrentBalance) : null;
  if (ledger !== null) return ledger;
  const initial = effectiveInitialBalance(debt);
  if (!gt(initial, ZERO)) return ZERO;
  if (date < effectiveStartDate(debt)) return entersByBalance(debt) ? initial : ZERO;
  return currentBalance(debt, date, now);
}

export const isActiveDebt = (t: Transaction): boolean => t.type === 'debt' && t.isActive;

/**
 * DebtProjector.drawdownDate: a loan's effective start. A debt entered by its current balance has
 * none: every scheduled payment counts against that balance.
 */
export const drawdownDate = (debt: Transaction): Day | null => (entersByBalance(debt) ? null : effectiveStartDate(debt));

/**
 * DebtProjector.paymentDates (R14, R15): a loan is paid on its drawdown day plus 1, 2, … periods
 * (anchored on the drawdown; its term ends it, not endAfterOccurrences); a current-balance debt
 * on its schedule from startDate within endAfterOccurrences. Both stop after endDate and before `end`.
 */
export function debtPaymentDates(debt: Transaction, end: Day): readonly Day[] {
  const drawdown = drawdownDate(debt);
  const step = frequencyStep(debt.frequency);
  if (step === null || step.count <= 0) return drawdown === null && debt.startDate < end ? [debt.startDate] : [];
  const anchor = drawdown ?? debt.startDate;
  const limit = drawdown === null ? (debt.endAfterOccurrences ?? Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
  const dates: Day[] = [];
  for (let n = drawdown === null ? 0 : 1; dates.length < limit; n++) {
    const date = scheduleDate(anchor, step, n);
    if (!(date < end) || (debt.endDate !== null && date > debt.endDate)) break;
    dates.push(date);
  }
  return dates;
}

/**
 * DebtProjector.payment(for:on:) (R14, R15): nothing on or before a loan's drawdown day or once
 * nothing is owed; the clearing payment (what was owed plus the month's interest) when the
 * balance is 0 after it; otherwise `amount`, the payment the balance assumes.
 */
export function debtPayment(debt: Transaction, date: Day, now: Day): Decimal {
  const drawdown = drawdownDate(debt);
  if (drawdown !== null && date <= drawdown) return ZERO;
  const owed = currentBalance(debt, addDays(date, -1), now);
  if (!gt(owed, ZERO)) return ZERO;
  const recorded = debt.actuals?.get(date);
  if (debt.debtIsInterestOnly === true) return recorded ?? fieldsOn(debt, date).amount;
  if (recorded !== undefined) return min(recorded, add(owed, mul(owed, monthlyRate(effectiveInterestRate(debt, date) ?? ZERO))));
  if (gt(currentBalance(debt, date, now), ZERO)) return regularPayment(debt, date, now);
  return add(owed, mul(owed, monthlyRate(effectiveInterestRate(debt, date) ?? ZERO)));
}

/** DebtProjector.totalPaid: Σ payments on the debt's payment dates before `end`, after `since`'s day when given. */
export const totalPaid = (debt: Transaction, end: Day, now: Day, since: Day | null = null): Decimal =>
  debtPaymentDates(debt, end).reduce<Decimal>(
    (total, date) => (since !== null && date <= since ? total : add(total, debtPayment(debt, date, now))),
    ZERO,
  );

/** round2(Σ remainingBalance) over active debts. */
export const totalCurrentDebt = (transactions: readonly Transaction[], date: Day, now: Day): Decimal =>
  round2(sum(transactions.filter(isActiveDebt).map((debt) => remainingBalance(debt, date, now))));

/**
 * DebtProjector.project (R15): per bucket, round2 of every active debt's balance on the bucket's
 * last day (after the payments dated in it), summed in input order; a debt in `settlements`
 * (debt id → sale date) is 0 from the start of the sale month (R2).
 */
export function projectDebts(
  transactions: readonly Transaction[],
  window: Window,
  now: Day,
  settlements: ReadonlyMap<Id, Day> = new Map(),
): Series {
  const debts = transactions.filter(isActiveDebt);
  return timeBuckets(window).map((bucket) => {
    const lastDay = addDays(bucket.end, -1);
    const total = debts.reduce<Decimal>((sum, debt) => {
      const saleDate = settlements.get(debt.id);
      return saleDate !== undefined && bucket.start >= startOfMonth(saleDate) ? sum : add(sum, currentBalance(debt, lastDay, now));
    }, ZERO);
    return { ...bucket, amount: round2(total) };
  });
}

/** The first payment date after which the debt owes nothing, having owed something before it. */
function clearingDate(debt: Transaction, from: Day, now: Day): Day | null {
  const horizon = addMonths(maxDay(maxDay(from, debt.startDate), effectiveStartDate(debt)), MAX_PAYOFF_MONTHS);
  const dates = debtPaymentDates(debt, horizon);
  if (dates.length === 0) return null;
  let owed = gt(currentBalance(debt, addDays(dates[0]!, -1), now), ZERO);
  for (const date of dates) {
    if (gt(currentBalance(debt, date, now), ZERO)) owed = true;
    else if (owed) return date;
  }
  return null;
}

/**
 * DebtProjector.calculatePayoffDate (R15): the date of the final payment on the debt's own
 * schedule (even when past), searched 600 months from max(startDate, the debt's start); null
 * when payments never clear it; startDate for a non-debt or nothing owed.
 */
export function payoffDate(debt: Transaction, startDate: Day, now: Day): Day | null {
  if (debt.type !== 'debt' || !gt(effectiveInitialBalance(debt), ZERO)) return startDate;
  return clearingDate(debt, startDate, now);
}

/** Index of the first bucket at or below 0 after a positive one: the month a debt line clears. */
export function firstClearBucket(series: Series): number | null {
  let sawPositive = false;
  for (const [index, bucket] of series.entries()) {
    if (gt(bucket.amount, ZERO)) sawPositive = true;
    else if (sawPositive) return index;
  }
  return null;
}

/**
 * DebtProjector.debtFreeDate (R15): in the first bucket of `series` at 0 after a positive one,
 * the latest date one of `debts` was cleared (its final payment or the sale that settled it,
 * the earlier) before the bucket's end; the bucket's start if none. null when it never clears.
 */
export function debtFreeDate(debts: readonly Transaction[], series: Series, settlements: ReadonlyMap<Id, Day>, now: Day): Day | null {
  const index = firstClearBucket(series);
  if (index === null) return null;
  const bucket = series[index]!;
  const cleared = debts.filter(isActiveDebt).flatMap((debt): Day[] => {
    const paidOff = clearingDate(debt, series[0]!.start, now);
    const saleDate = settlements.get(debt.id);
    const date = saleDate === undefined ? paidOff : paidOff === null ? saleDate : minDay(paidOff, saleDate);
    return date === null ? [] : [date];
  });
  const before = cleared.filter((date) => date < bucket.end);
  return before.length === 0 ? bucket.start : before.reduce(maxDay);
}

/** round2 of the interest accrued across the window's months (legacy initialBalance debts only). */
export function totalInterestPaid(debt: Transaction, window: Window, now: Day): Decimal {
  if (debt.type !== 'debt' || debt.initialBalance === null || !gt(debt.initialBalance, ZERO)) return ZERO;
  let balance = currentBalance(debt, window.start, now);
  if (!gt(balance, ZERO)) return ZERO;

  const rate = debtMonthlyRate(debt);
  const months = elapsedMonths(window.start, window.end);
  const alreadyPaid = elapsedMonths(debt.startDate, window.start);
  let interest: Decimal = ZERO;
  for (let month = 0; month < months && gt(balance, ZERO); month++) {
    if (debt.endAfterOccurrences !== null && alreadyPaid + month >= debt.endAfterOccurrences) break;
    if (month > 0) {
      const accrued = mul(balance, rate);
      interest = add(interest, accrued);
      balance = add(balance, accrued);
    }
    balance = max(sub(balance, debt.amount), ZERO);
  }
  return round2(interest);
}

/** round2 of every future payment until payoff; after 600 months the remainder is added. */
export function totalDebtCost(debt: Transaction, startDate: Day, now: Day): Decimal {
  let balance = currentBalance(debt, startDate, now);
  if (!gt(balance, ZERO)) return ZERO;

  const rate = debtMonthlyRate(debt);
  let paid: Decimal = ZERO;
  for (let month = 0; month < MAX_PAYOFF_MONTHS && gt(balance, ZERO); month++) {
    if (month > 0) balance = add(balance, mul(balance, rate));
    const payment = min(debt.amount, balance);
    balance = max(sub(balance, payment), ZERO);
    paid = add(paid, payment);
  }
  return round2(gt(balance, ZERO) ? add(paid, balance) : paid);
}
